import { z } from 'zod'
import { questionAuthorTypeSchema } from '../enums/question-author-type.enum.js'
import type { QaSessionGate } from '../enums/signing-session-gate.enum.js'
import { ENVELOPE_LIMITS } from '../constants/envelope.js'
import { envelopeStatusSchema } from '../enums/envelope-status.enum.js'
import { tenantModeSchema } from '../enums/tenant-mode.enum.js'
import { brandDtoSchema } from './brand.dto.js'
import { documentDtoSchema } from './envelope.dto.js'
import { uuidSchema } from '../validators/params.js'

export const questionMessageDtoSchema = z.object({
  id: uuidSchema,
  authorType: questionAuthorTypeSchema,
  authorId: uuidSchema,
  authorName: z.string(),
  body: z.string(),
  createdAt: z.string().datetime(),
})

export type QuestionMessageDto = z.infer<typeof questionMessageDtoSchema>

export const questionThreadDtoSchema = z.object({
  id: uuidSchema,
  documentId: uuidSchema,
  askerId: uuidSchema,
  answererId: uuidSchema,
  page: z.number().int().positive(),
  x: z.number(),
  y: z.number(),
  quote: z.string().nullable(),
  messages: z.array(questionMessageDtoSchema),
  createdAt: z.string().datetime(),
})

export type QuestionThreadDto = z.infer<typeof questionThreadDtoSchema>

export const presenceViewerDtoSchema = z.object({
  id: uuidSchema,
  name: z.string(),
  kind: z.enum(['signer', 'answerer', 'staff']),
})

export type PresenceViewerDto = z.infer<typeof presenceViewerDtoSchema>

export const createQuestionInputSchema = z
  .object({
    documentId: uuidSchema,
    page: z.number().int().min(1),
    x: z.number().min(0).max(100),
    y: z.number().min(0).max(100),
    quote: z.string().trim().max(ENVELOPE_LIMITS.maxQuestionQuoteChars).nullish(),
    body: z.string().trim().min(1).max(ENVELOPE_LIMITS.maxQuestionChars),
  })
  .strict()

export type CreateQuestionInput = z.infer<typeof createQuestionInputSchema>

export const replyQuestionInputSchema = z
  .object({
    body: z.string().trim().min(1).max(ENVELOPE_LIMITS.maxQuestionChars),
  })
  .strict()

export type ReplyQuestionInput = z.infer<typeof replyQuestionInputSchema>

export const questionThreadListDtoSchema = z.object({
  threads: z.array(questionThreadDtoSchema),
})

export type QuestionThreadListDto = z.infer<typeof questionThreadListDtoSchema>

export const questionSseEventSchema = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('question.created'),
    thread: questionThreadDtoSchema,
  }),
  z.object({
    type: z.literal('question.replied'),
    threadId: uuidSchema,
    askerId: uuidSchema,
    answererId: uuidSchema,
    message: questionMessageDtoSchema,
  }),
  z.object({
    type: z.literal('presence'),
    viewers: z.array(presenceViewerDtoSchema),
  }),
])

export type QuestionSseEvent = z.infer<typeof questionSseEventSchema>

const qaShared = {
  token: z.string().min(1),
  consentVersion: z.string(),
  consentText: z.string(),
  envelope: z.object({
    id: uuidSchema,
    title: z.string(),
    status: envelopeStatusSchema,
    expiresAt: z.string().datetime(),
    /** `test` pages say "Test envelope: not legally binding". */
    mode: tenantModeSchema,
  }),
  brand: brandDtoSchema,
  answerer: z.object({
    id: uuidSchema,
    name: z.string(),
    email: z.string().email(),
    relationship: z.string(),
  }),
}

export const qaSessionDtoSchema = z.discriminatedUnion('gate', [
  z.object({
    ...qaShared,
    gate: z.literal('consent' satisfies QaSessionGate),
    unavailableReason: z.null(),
    documents: z.array(documentDtoSchema).max(0),
    threads: z.array(questionThreadDtoSchema).max(0),
    viewers: z.array(presenceViewerDtoSchema).max(0),
  }),
  z.object({
    ...qaShared,
    gate: z.literal('ready' satisfies QaSessionGate),
    unavailableReason: z.null(),
    documents: z.array(documentDtoSchema),
    threads: z.array(questionThreadDtoSchema),
    viewers: z.array(presenceViewerDtoSchema),
  }),
  z.object({
    ...qaShared,
    gate: z.literal('unavailable' satisfies QaSessionGate),
    unavailableReason: z.string(),
    documents: z.array(documentDtoSchema).max(0),
    threads: z.array(questionThreadDtoSchema).max(0),
    viewers: z.array(presenceViewerDtoSchema).max(0),
  }),
])

export type QaSessionDto = z.infer<typeof qaSessionDtoSchema>
